import type { App, ComponentPublicInstance } from 'vue'
import type { SerializableHead } from '@unhead/vue/types'
import type { ErrorReport, PipeEventProcessorDef, PipeInterface } from '@render/composables/usePipe'
import { PIPE_EVENTS } from '@render/composables/usePipe'
import type { PipeIntegration, PipeIntegrationDef } from '../index'
import Bugsnag, { type NotifiableError, type BreadcrumbType, type Event } from '@bugsnag/js'
import { attachPipeErrInfo, computeGrouping, readPipeErrInfo } from './grouping'
import { extractErrorFields, toError } from './describeError'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __bugsnagEarlyErrors?: ErrorReport[]
    Bugsnag?: typeof Bugsnag
  }
}

let registeredApp: App | null = null
let vueHandlerInstalled = false

const getComponentName = (instance: ComponentPublicInstance | null): string | undefined => {
  if (!instance) return undefined
  const opts = instance.$options as Record<string, unknown>
  return (opts.name as string) || (opts.__name as string) || (opts.__file as string)
}

const installVueHandler = (app: App) => {
  if (vueHandlerInstalled) return
  app.config.errorHandler = (err, instance, info) => {
    if (Bugsnag.isStarted()) {
      Bugsnag.notify(err as NotifiableError, (event) => {
        event.severity = 'error'
        event.unhandled = true
        event.addMetadata('vue', {
          errorInfo: info,
          componentName: getComponentName(instance),
        })
      })
    }
    // Keep devtools/console aware, but do NOT re-throw — re-throwing triggers
    // Nuxt's error page on production static builds.
    console.error('[Vue error]', err, info)
  }
  vueHandlerInstalled = true
}

export const registerBugsnagVueApp = (app: App) => {
  registeredApp = app
  if (Bugsnag.isStarted()) installVueHandler(app)
}

const BREADCRUMB_TYPES: readonly BreadcrumbType[] = [
  'error',
  'log',
  'manual',
  'navigation',
  'process',
  'request',
  'state',
  'user',
]

const toBreadcrumbType = (raw: string | undefined): BreadcrumbType =>
  BREADCRUMB_TYPES.includes(raw as BreadcrumbType) ? (raw as BreadcrumbType) : 'manual'

interface BugsnagConfig {
  apiKey: string
  releaseStage?: string
  appVersion?: string
  enabledReleaseStages?: string
}

interface NavigationPayload {
  pageId: string
  path: string
  title: string
  timestamp: number
}

interface BugsnagNotifyEventData {
  message?: string
  severity?: 'info' | 'warning' | 'error'
  metadata?: Record<string, unknown>
  context?: string
}

interface BugsnagBreadcrumbEventData {
  message?: string
  type?: string
  metadata?: Record<string, unknown>
}

const parseStages = (raw?: string): string[] | undefined => {
  if (!raw) return undefined
  const stages = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  return stages.length > 0 ? stages : undefined
}

const bugsnagIntegration: PipeIntegration = (): PipeIntegrationDef => {
  return {
    getHead(): SerializableHead {
      // Pre-hydration capture (before JS-bundle arrives):
      // Add event listeners related to errors.
      // Drop benign `ResizeObserver loop` notices (browser layout-timing
      // warnings, not app errors) before reportError, so they reach neither
      // Bugsnag nor listener-based monitors like Clarity.
      // Patch global fetch to push errors into early errors before Bugsnag starts.
      // Set tagPriority to 'high' so this runs before other integration
      // scripts from head and catch their errors also.
      // Inline `<head>` script — every byte ships to every visitor, so no
      // comments inside the template literal. Purpose:
      //
      // `reportError(entry)` is the single sink for everything our
      // `addEventListener('error' | 'unhandledrejection')` and patched fetch
      // produce. Pre-Bugsnag-start: queue in `window.__bugsnagEarlyErrors`
      // for the bundle to drain (see `setup()` below). Post-start: notify
      // Bugsnag directly — we own this listener (installed in capture phase
      // before any 3rd-party script can overwrite `window.onerror`) and
      // Bugsnag's own monkey-patched onerror is disabled via
      // `enabledErrorTypes` in `Bugsnag.start`, so this is the only path.
      // The `instanceof Error` coerce wraps non-Error throws from 3rd-party
      // code. Resource (image/script/stylesheet load failures) keeps the
      // `[Pre-hydration] Resource` label regardless of start state; non-
      // resource error + unhandledrejection switch to `[Runtime] *` labels
      // post-start. Context strings here mirror `ERROR_CONTEXTS` in
      // `usePipe.ts` — keep both sides in sync.
      //
      // `reportError` also attaches the error info to the error object as
      // `error.__pipeErrInfo` (= `PIPE_ERR_KEY` in grouping.ts — keep the
      // literal in sync). The bundle's config `onError` reads that to build the
      // per-brand `groupingHash` + title, so groupingHash is NOT set here. The
      // fetch patch tags `err.__bugsnagReported` before re-throwing so the
      // `unhandledrejection` listener can skip the same network failure (avoids
      // double-counting it under both `Fetch` and `Unhandled Rejection`).
      return {
        script: [
          {
            tagPriority: 'high',
            tagPosition: 'head',
            innerHTML: `
              (function(){
                window.__bugsnagEarlyErrors = window.__bugsnagEarlyErrors || [];
                function reportError(entry) {
                  if (!(entry && entry.error instanceof Error)) {
                    entry.error = new Error(typeof entry.error === 'string' ? entry.error : entry.context);
                  }
                  try {
                    entry.error.__pipeErrInfo = Object.assign({ category: entry.context }, entry.metadata);
                  } catch (e) {}
                  if (window.Bugsnag && window.Bugsnag.isStarted()) {
                    window.Bugsnag.notify(entry.error, function(event) {
                      event.severity = 'error';
                      event.unhandled = !!(entry.metadata && entry.metadata.unhandled);
                      if (entry.metadata) event.addMetadata('metadata', entry.metadata);
                    });
                    return;
                  }
                  window.__bugsnagEarlyErrors.push(entry);
                }
                window.addEventListener('error', function(e) {
                  var t = e.target, isResource = t && t !== window && t.tagName;
                  if (!isResource && e.message && e.message.indexOf('ResizeObserver loop') === 0) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    return;
                  }
                  if (isResource && t.tagName === 'IMG') return;
                  if (!isResource && !e.error && e.message === 'Script error.') return;
                  var started = window.Bugsnag && window.Bugsnag.isStarted();
                  if (isResource) {
                    reportError({
                      error: e.message || ('Failed to load ' + t.tagName),
                      context: '[Pre-hydration] Resource',
                      metadata: { unhandled: true, tag: t.tagName, src: t.src || t.href }
                    });
                  } else {
                    reportError({
                      error: e.error || e.message,
                      context: started ? '[Runtime] Error' : '[Pre-hydration] Error',
                      metadata: { unhandled: true, filename: e.filename, lineno: e.lineno, colno: e.colno }
                    });
                  }
                }, true);
                window.addEventListener('unhandledrejection', function(e) {
                  if (e.reason && e.reason.__bugsnagReported) return;
                  var started = window.Bugsnag && window.Bugsnag.isStarted();
                  reportError({
                    error: e.reason,
                    context: started ? '[Runtime] Unhandled Rejection' : '[Pre-hydration] Unhandled Rejection',
                    metadata: { unhandled: true, reasonType: typeof e.reason, reason: !(e.reason instanceof Error) && e.reason != null ? String(e.reason) : undefined }
                  });
                });
                var origFetch = window.fetch;
                if (origFetch) {
                  window.fetch = function() {
                    var args = arguments;
                    return origFetch.apply(this, args).catch(function(err) {
                      var started = window.Bugsnag && window.Bugsnag.isStarted();
                      var input = args[0];
                      var options = args[1];
                      var url = typeof input === 'string' ? input : input && input.url;
                      var method = (input && input.method) || (options && options.method) || 'GET';
                      reportError({
                        error: err,
                        context: started ? '[Runtime] Fetch' : '[Pre-hydration] Fetch',
                        metadata: { unhandled: false, url: url, method: method }
                      });
                      try { Object.defineProperty(err, '__bugsnagReported', { value: true }); } catch (e) {}
                      throw err;
                    });
                  };
                }
              })();
            `,
          },
        ],
      }
    },

    setup(pipe: PipeInterface, config: BugsnagConfig) {
      if (typeof window === 'undefined') return
      if (!config.apiKey) {
        console.warn('[Extension] Bugsnag apiKey missing — skipping init')
        return
      }

      // Decorate every Bugsnag event with brand/distribution context AND the
      // per-brand groupingHash + dashboard title. Brand context goes on three
      // surfaces: the title (brandLabel = brandCode, or the serving domain when a
      // pipe has no code), the `brand`/`distribution` filter facets, and a `pipe`
      // metadata tab carrying the full domain/locale/build detail. The grouping
      // itself is computed from the error info our capture path attached to the
      // error object (see grouping.ts). Called from the config `onError` below so
      // it is registered with `Bugsnag.start` (Bugsnag's recommendation) and is
      // therefore active before the early-error buffer is drained.
      const applyBrandAndGrouping = (event: Event) => {
        const servingHost = window.location.hostname
        const brandCode = pipe.getData('const.system.brandCode')
        const distributionPath = pipe.getData('const.system.distributionPath')
        const brandLabel = brandCode ? String(brandCode) : servingHost

        event.addMetadata('pipe', {
          brandCode,
          servingHost,
          distributionPath,
          distributionName: pipe.getData('const.system.distributionName'),
          geo: pipe.getData('const.system.geography'),
          language: pipe.getData('const.system.language'),
          pipeId: pipe.getData('const.system.pipeId'),
          buildId: pipe.getData('const.system.buildId'),
        })

        if (brandCode) event.addFeatureFlag('brand', String(brandCode))
        if (distributionPath) event.addFeatureFlag('distribution', String(distributionPath))

        const info = readPipeErrInfo(event.originalError)
        // Errors with no attached info (Vue handler, bugsnag_notify processor,
        // external direct notifies) fall through to Bugsnag's default grouping.
        if (info) {
          try {
            const { groupingHash, errorClass, url, method } = computeGrouping({
              info,
              event,
              brandLabel,
            })
            event.groupingHash = groupingHash
            if (event.errors[0]) event.errors[0].errorClass = errorClass
            // Standard `url` / `method` props on the `metadata` tab, normalized across every error type
            if (url) event.addMetadata('metadata', { url })
            if (method) event.addMetadata('metadata', { method })
          } catch (e) {
            console.warn('[Bugsnag] grouping computation failed; using default grouping', e)
          }
        }
      }

      if (!Bugsnag.isStarted()) {
        Bugsnag.start({
          apiKey: config.apiKey,
          releaseStage: config.releaseStage || undefined,
          appVersion: config.appVersion || undefined,
          enabledReleaseStages: parseStages(config.enabledReleaseStages),
          // Our pre-hydration addEventListener('error' / 'unhandledrejection')
          // listeners are the single source of truth. They install in capture
          // phase before any 3rd-party script and route directly to
          // Bugsnag.notify post-start (see getHead inline script above).
          // Disabling Bugsnag's built-in window.onerror plugin avoids both
          // (a) duplicate reports when both handlers fire, and (b) silent
          // drops when a later 3rd-party script overwrites window.onerror
          // (the GTM/Clarity scenario that motivated this change).
          enabledErrorTypes: {
            unhandledExceptions: false,
            unhandledRejections: false,
          },
          onError(event) {
            // Skip 'ResizeObserver loop' noise, which is safe, as it means
            // callback changed layout within the same frame. Not an error.
            if ((event.errors[0]?.errorMessage ?? '').startsWith('ResizeObserver loop')) {
              return false
            }

            // Suppress noise from third-party analytics/tracking SDKs — these
            // fail due to ad-blockers, CDN blips, or browser privacy settings,
            // none of which we can fix. Covers both runtime fetch failures
            // (info.url) and pre-hydration script load failures (info.src).
            //
            // Read from readPipeErrInfo(event.originalError), NOT
            // event.getMetadata('metadata'): this config-level onError runs
            // as part of Bugsnag's global callback list, which always runs
            // BEFORE the local callback passed to the `Bugsnag.notify(err, cb)`
            // call that reports the error (see client.js: `[].concat(this._cbs.e)
            // .concat(onError)`). That local callback is what calls
            // `event.addMetadata('metadata', ...)`, so metadata is always empty
            // here. The pipe-err-info property on the error itself is set
            // synchronously before notify() is ever called, so it's the only
            // thing reliably visible at this point (same reasoning as
            // computeGrouping — see grouping.ts).
            const THIRD_PARTY_ANALYTICS_HOSTS = [
              '.ecs.us-west-1.on.aws', // Klaviyo (AWS)
              '.run.app/events', // Klaviyo (GCP Cloud Run)
              'static-tracking.klaviyo.com', // Klaviyo static assets
              'cdn.segment.com', // Segment
              'google-analytics.com', // GA4 (classic hyphenated domain)
              'analytics.google.com', // GA4 (Google-tag/consent-mode domain)
              'google.com/g/collect', // GA4 (alternate endpoint)
              'googletagmanager.com', // GTM
              'connect.facebook.net', // Meta Pixel config
              'facebook.com/tr', // Meta Pixel events
              'j.clarity.ms', // Microsoft Clarity
            ]
            const info = readPipeErrInfo(event.originalError)
            const resourceUrl = String(info?.url ?? info?.src ?? '')
            if (
              resourceUrl &&
              THIRD_PARTY_ANALYTICS_HOSTS.some((host) => resourceUrl.includes(host))
            ) {
              return false
            }

            // Third-party SDKs (Klaviyo, Facebook Pixel) reject promises with
            // non-Error values (Response objects, strings, status codes) from
            // their internal fetch chains. Bugsnag surfaces these as
            // "InvalidError · unhandledrejection handler received a non-error".
            // Demote to info rather than suppress: still captured so a spike
            // from a broken SDK is visible, but won't trigger error-level alerts.
            if (
              event.errors[0]?.errorClass === 'InvalidError' &&
              event.errors[0]?.errorMessage?.includes(
                'unhandledrejection handler received a non-error',
              )
            ) {
              event.severity = 'info'
              event.unhandled = false
            }

            applyBrandAndGrouping(event)
          },
        })
      }

      // Exposed for inline non-module scripts (RAW integrations, 3rd-party SDK
      // callbacks, devtools debugging) that can't `import Bugsnag`. Module-scope
      // code should keep importing directly.
      window.Bugsnag = Bugsnag

      if (registeredApp) installVueHandler(registeredApp)

      // Dev / local testing: prefix context with DEV_TEST: so dev errors are
      // filterable. Registered BEFORE the drain so pre-bundle queued errors
      // get the prefix too (addOnError applies to every Bugsnag.notify call
      // after this point).
      const appEnv = pipe.getData('const.system.appEnv')
      const hostname = window.location.hostname
      const isLocalHost =
        /^(localhost|127\.|0\.0\.0\.0|192\.168\.|10\.)/.test(hostname) ||
        hostname.endsWith('.local')
      const isDevTest = appEnv === 'development' || isLocalHost
      if (isDevTest) {
        Bugsnag.addOnError((event) => {
          event.context = `DEV_TEST: ${event.context ?? ''}`.trim()
          event.addMetadata('metadata', { appEnv })
        })
      }

      const notifyBugsnag = (report: ErrorReport) => {
        // Most rejections reaching here are plain objects (useFetch's FetchError,
        // third-party SDK payloads), not Errors — `toError` reads their message
        // instead of stringifying them to "[object Object]", which used to become
        // both the event message and the grouping title. See describeError.ts.
        const err = toError(report.error)
        // Diagnostics that only exist on the original object (status, endpoint,
        // timedOut, response body) — lost once it was coerced to an Error.
        const errorFields = extractErrorFields(report.error)
        // Attach the error info so the config onError grouping can read them.
        // No-op if the inline capture script already attached them (drained early
        // errors); set here for runtime app errors (pipe.trackError), whose
        // category is an ERROR_CONTEXTS value — computeGrouping folds the brand and
        // failure message into those instead of one bucket per context.
        attachPipeErrInfo({
          error: err,
          info: { category: report.context, ...(report.metadata ?? {}) },
        })
        Bugsnag.notify(err as NotifiableError, (event) => {
          // Leave event.context as Bugsnag's default (the current page path) — the
          // category already leads the grouping title (errorClass) via
          // computeGrouping, so prepending it here would just duplicate it.
          event.severity = 'error'
          event.unhandled = !!report.metadata?.unhandled
          // Error-derived fields first so an explicit call-site metadata value of
          // the same name wins the merge.
          if (errorFields) event.addMetadata('metadata', errorFields)
          if (report.metadata) event.addMetadata('metadata', report.metadata)
        })
      }

      // Process the pre-bundle queued early errors added before Bugsnag started.
      for (const entry of window.__bugsnagEarlyErrors ?? []) notifyBugsnag(entry)
      window.__bugsnagEarlyErrors = []

      // Process live runtime errors via the pipe event bus.
      // This allows integration to be isolated from other pipe code.
      pipe.subscribe(PIPE_EVENTS.errorTrack, notifyBugsnag)

      console.log('[Extension] Bugsnag initialized for release stage:', config.releaseStage)

      pipe.subscribe('navigation:show', (payload: NavigationPayload) => {
        Bugsnag.leaveBreadcrumb(
          `Navigated to ${payload.path}`,
          {
            pageId: payload.pageId,
            title: payload.title,
            timestamp: payload.timestamp,
          },
          'navigation',
        )
        Bugsnag.setContext(payload.path)
      })
    },

    getEventProcessors(): PipeEventProcessorDef[] {
      return [
        {
          type: 'bugsnag_notify',
          handler: async ({ data }) => {
            // Processors are registered in getEventProcessors() before setup()
            // runs Bugsnag.start(), so guard against firing pre-start.
            if (!Bugsnag.isStarted()) return
            const payload = (data as BugsnagNotifyEventData | undefined) ?? {}
            const message = payload.message || 'Bugsnag notify'
            Bugsnag.notify(new Error(message) as NotifiableError, (event) => {
              event.severity = payload.severity ?? 'info'
              if (payload.context) event.context = payload.context
              if (payload.metadata) event.addMetadata('custom', payload.metadata)
            })
          },
        },
        {
          type: 'bugsnag_breadcrumb',
          handler: async ({ data }) => {
            if (!Bugsnag.isStarted()) return
            const payload = (data as BugsnagBreadcrumbEventData | undefined) ?? {}
            Bugsnag.leaveBreadcrumb(
              payload.message || 'breadcrumb',
              payload.metadata,
              toBreadcrumbType(payload.type),
            )
          },
        },
      ]
    },
  }
}

export default bugsnagIntegration
